import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import JobsiteApi from '../jobsite/jobsiteApi';
import { Jobsite, JobsiteAndProject } from './jobsiteType';
import ProjectApi from '../project/projectApi';
import { Project } from '../project/projectType';
import { GoogleMap, useLoadScript, Marker, Autocomplete } from '@react-google-maps/api';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import Spinner from '../components/ui/spinner';
import { Save, MapPin } from 'lucide-react';
import { getCurrentTimestamp, formatAddress } from '../lib/global_function';

const UpdateJobsitePage: React.FC = () => {
    const breadcrumbs = [
        { label: 'Project', path: '/main/project' },
        { label: 'Jobsite', path: '/main/project/jobsite' },
        { label: 'Update', path: '/main/project/jobsite/update' },
    ];

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
        libraries: ['places'],
    });

    const location = useLocation();
    const jobsiteData = location.state?.jobsiteAndProject as JobsiteAndProject;
    const navigate = useNavigate();
    const updateTimeStamp = getCurrentTimestamp();

    const [projects, setProjects] = useState<Project[]>([]);
    const [mapCenter, setMapCenter] = useState({ lat: 28.0517, lng: -82.4588 });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);

    const [address, setAddress] = useState(
        jobsiteData ?
            formatAddress(
                jobsiteData.address_1,
                jobsiteData.address_2 || '',
                jobsiteData.city,
                jobsiteData.state,
                jobsiteData.zip
            ) : ''
    );

    const [jobsite, setJobsite] = useState<Partial<Jobsite>>(
        jobsiteData ? jobsiteData : {
            name: '',
            jobsite_description: '',
            project_id: '',
            project_name: '',
            company_name: '',
            address_1: '',
            address_2: '',
            city: '',
            state: '',
            zip: '',
            updated_at: updateTimeStamp,
        }
    );

    useEffect(() => {
        if (!jobsiteData) {
            setErrorMessage('Jobsite not available, please select a jobsite by clicking Go To Jobsite button.');
        }
    }, [jobsiteData]);

    const updateMap = useCallback(() => {
        if (!jobsite) return;

        const { address_1, city, state, zip } = jobsite;
        if (address_1 && city && state && zip) {
            const fullAddress = `${address_1}, ${city}, ${state} ${zip}`;
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address: fullAddress }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const location = results[0].geometry.location;
                    setMapCenter({ lat: location.lat(), lng: location.lng() });
                } else {
                    console.error('Geocode was not successful for the following reason: ' + status);
                }
            });
        }
    }, [jobsite]);

    useEffect(() => {
        if (isLoaded && jobsite) {
            updateMap();
        }
    }, [isLoaded, updateMap]);

    useEffect(() => {
        const fetchProject = async () => {
            try {
                const fetchedProjects = await ProjectApi.get();
                setProjects(fetchedProjects);
            } catch (error) {
                if (error instanceof Error) {
                    setErrorMessage(error.message);
                } else {
                    setErrorMessage('An unknown error occurred.');
                }
            }
        };
        fetchProject();
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setJobsite(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrorMessage(null);
        setIsSubmitting(true);

        const updateTimeStamp = getCurrentTimestamp();
        try {
            await JobsiteApi.update(String(jobsite.jobsite_id), { ...jobsite, updated_at: updateTimeStamp } as Jobsite);
            setMessage('Jobsite updated successfully');

            // Redirect after a short delay
            //setTimeout(() => {
            //    navigate('/main/project/jobsite');
            //}, 2000);

        } catch (error) {
            if (error instanceof Error) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage('An unknown error occurred.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePlaceSelect = () => {
        if (autocomplete) {
            const place = autocomplete.getPlace();
            if (place.formatted_address) {
                setAddress(place.formatted_address);

                const newJobsite = { ...jobsite };
                for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
                    const componentType = component.types[0];
                    switch (componentType) {
                        case "street_number":
                            newJobsite.address_1 = component.long_name;
                            break;
                        case "route":
                            newJobsite.address_1 += " " + component.short_name;
                            break;
                        case "postal_code":
                            newJobsite.zip = component.long_name;
                            break;
                        case "locality":
                            newJobsite.city = component.long_name;
                            break;
                        case "administrative_area_level_1":
                            newJobsite.state = component.long_name;
                            break;
                    }
                }
                setJobsite(newJobsite);

                // Update map center
                if (place.geometry && place.geometry.location) {
                    setMapCenter({
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng()
                    });
                }
            }
        }
    };

    const handleNavigateToJobsiteList = () => {
        navigate('/main/project/jobsite');
    };

    const handleCancel = () => {
        navigate('/main/project/jobsite');
    };

    if (!isLoaded) {
        return (
            <>
                <HorizontalMenu />
                <Spinner />
            </>
        );
    }

    return (
        <>
            <HorizontalMenu />
            <div className="container-safe py-6">
                <Breadcrumbs items={breadcrumbs} />

                <div className="mt-6">
                    <h1 className="text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
                        <MapPin className="size-7 text-primary" />
                        Update Jobsite
                    </h1>

                    {/* Error Message - No jobsite data */}
                    {errorMessage && !jobsiteData && (
                        <div className="mb-6">
                            <Alert variant="error">
                                {errorMessage}
                            </Alert>
                            <div className="mt-4">
                                <Button size="medium" onClick={handleNavigateToJobsiteList}>
                                    Go To Jobsite
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Success Message */}
                    {message && (
                        <Alert variant="success" className="mb-6">
                            {message}
                        </Alert>
                    )}

                    {/* Update Form */}
                    {!message && jobsiteData && (
                        <div className="space-y-6">
                            {/* Error Message - During update */}
                            {errorMessage && jobsiteData && (
                                <Alert variant="error">
                                    {errorMessage}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Jobsite Information Card */}
                                <div className="p-6 bg-white border border-gray-200 rounded-lg">
                                    <h2 className="text-lg font-semibold text-foreground mb-4">Jobsite Information</h2>

                                    <div className="space-y-4">
                                        {/* Jobsite Name */}
                                        <div>
                                            <Label htmlFor="name">
                                                Jobsite Name
                                                <span className="text-red-600 ml-1">*</span>
                                            </Label>
                                            <Input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={jobsite.name || ''}
                                                onChange={handleInputChange}
                                                placeholder="Enter jobsite name"
                                                required
                                            />
                                        </div>

                                        {/* Jobsite Description */}
                                        <div>
                                            <Label htmlFor="jobsite_description">
                                                Description
                                                <span className="text-red-600 ml-1">*</span>
                                            </Label>
                                            <Input
                                                type="text"
                                                id="jobsite_description"
                                                name="jobsite_description"
                                                value={jobsite.jobsite_description || ''}
                                                onChange={handleInputChange}
                                                placeholder="Enter description"
                                                required
                                            />
                                        </div>

                                        {/* Project Selection */}
                                        <div>
                                            <Label htmlFor="project_id">
                                                Project
                                                <span className="text-red-600 ml-1">*</span>
                                            </Label>
                                            <Select
                                                id="project_id"
                                                name="project_id"
                                                value={jobsite.project_id || ''}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select a project</option>
                                                {projects.map((project) => (
                                                    <option key={project.project_id} value={project.project_id}>
                                                        {project.name}
                                                    </option>
                                                ))}
                                            </Select>
                                        </div>
                                    </div>
                                </div>

                                {/* Location Card with Google Maps */}
                                <div className="p-6 bg-white border border-gray-200 rounded-lg">
                                    <h2 className="text-lg font-semibold text-foreground mb-4">Jobsite Location</h2>

                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="address">
                                                Search Address
                                                <span className="text-red-600 ml-1">*</span>
                                            </Label>
                                            <Autocomplete
                                                onLoad={(auto) => setAutocomplete(auto)}
                                                onPlaceChanged={handlePlaceSelect}
                                            >
                                                <Input
                                                    type="text"
                                                    id="address"
                                                    name="address"
                                                    value={address}
                                                    onChange={(e) => setAddress(e.target.value)}
                                                    placeholder="Search for an address"
                                                    required
                                                    className="mb-4"
                                                />
                                            </Autocomplete>

                                            {/* Current Address Display */}
                                            {jobsite.address_1 && (
                                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                                                    <p className="text-sm font-semibold text-gray-700 mb-1">Current Address:</p>
                                                    <p className="text-sm text-foreground">
                                                        {jobsite.address_1}
                                                        {jobsite.address_2 && `, ${jobsite.address_2}`}
                                                    </p>
                                                    <p className="text-sm text-foreground">
                                                        {jobsite.city}, {jobsite.state} {jobsite.zip}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Google Map */}
                                            <div className="border border-gray-300 rounded-lg overflow-hidden">
                                                <GoogleMap
                                                    center={mapCenter}
                                                    zoom={11}
                                                    mapContainerStyle={{ height: '400px', width: '100%' }}
                                                >
                                                    <Marker position={mapCenter} />
                                                </GoogleMap>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-wrap gap-3">
                                    <Button
                                        type="submit"
                                        size="medium"
                                        disabled={isSubmitting}
                                    >
                                        <Save size={18} />
                                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                    <Button
                                        type="button"
                                        size="medium"
                                        variant="inverted"
                                        onClick={handleCancel}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default UpdateJobsitePage;